import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Save, X, Plus, ChevronLeft, ChevronRight, Check } from 'lucide-react';
import { ProTopBar } from '../components/ProTopBar';
import { WorkoutSessionEditor } from '../components/WorkoutSessionEditor';
import {
  useAthletes,
  useCoachProgramFull,
  useCreateCoachProgram,
  useBulkSaveCoachProgram,
} from '../lib/queries';
import { useRole } from '../lib/role';
import type {
  BulkSessionInput,
  ProgramFull,
  WorkoutProgramStatus,
  CreateProgramBody,
  BulkSaveBody,
} from '../lib/api';
import { ApiError } from '../lib/api';
import styles from './Dashboard.module.css';

type Step = 1 | 2 | 3 | 4;

const STEPS: Array<{ n: Step; label: string }> = [
  { n: 1, label: 'Atleta & meta' },
  { n: 2, label: 'Schedule' },
  { n: 3, label: 'Esercizi' },
  { n: 4, label: 'Revisione' },
];

const GOAL_OPTIONS = [
  { value: 'massa', label: 'Massa' },
  { value: 'forza', label: 'Forza' },
  { value: 'ipertrofia', label: 'Ipertrofia' },
  { value: 'definizione', label: 'Definizione' },
  { value: 'mantenimento', label: 'Mantenimento' },
  { value: 'powerlifting', label: 'Powerlifting' },
  { value: 'performance', label: 'Performance' },
];

interface DraftMeta {
  athlete_id: string;
  name: string;
  goal: string;
  weeks_total: number;
  start_date: string;
  end_date: string;
  status: WorkoutProgramStatus;
}

function emptyMeta(): DraftMeta {
  return {
    athlete_id: '',
    name: '',
    goal: '',
    weeks_total: 4,
    start_date: '',
    end_date: '',
    status: 'draft',
  };
}

function newSessionDraft(week_n: number, day_n: number): BulkSessionInput {
  return {
    id: `new-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    week_n,
    day_n,
    name: '',
    focus: null,
    scheduled_date: null,
    scheduled_time: null,
    duration_estimated_min: 60,
    coach_note: null,
    exercises: [],
  };
}

function programToDraft(p: ProgramFull): { meta: DraftMeta; sessions: BulkSessionInput[] } {
  return {
    meta: {
      athlete_id: p.athlete_id,
      name: p.name,
      goal: p.goal ?? '',
      weeks_total: p.weeks_total,
      start_date: p.start_date ?? '',
      end_date: p.end_date ?? '',
      status: p.status,
    },
    sessions: p.sessions.map((s) => ({
      id: s.id,
      week_n: s.week_n,
      day_n: s.day_n,
      name: s.name,
      focus: s.focus,
      scheduled_date: s.scheduled_date,
      scheduled_time: s.scheduled_time,
      duration_estimated_min: s.duration_estimated_min,
      coach_note: s.coach_note,
      exercises: s.exercises.map((e) => ({
        id: e.id,
        position: e.position,
        name: e.name,
        variant: e.variant,
        sets_planned: e.sets_planned,
        reps_target: e.reps_target,
        rest_seconds: e.rest_seconds,
        notes: e.notes,
      })),
    })),
  };
}

/**
 * Sprint 7.2 — Wizard editor programma (coach).
 * 4 step: (1) atleta+meta, (2) schedule settimanale, (3) esercizi per sessione, (4) revisione+save.
 */
export default function WorkoutProgramEditor() {
  const { role } = useRole();
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const [search] = useSearchParams();
  const isEdit = !!id && id !== 'new';
  const preselectAthleteId = search.get('athlete_id');

  const [step, setStep] = useState<Step>(1);
  const [meta, setMeta] = useState<DraftMeta>(emptyMeta);
  const [sessions, setSessions] = useState<BulkSessionInput[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loadedFromServer, setLoadedFromServer] = useState<boolean>(false);

  const { data: athletes, isLoading: athletesLoading } = useAthletes(role === 'coach');
  const programFullQuery = useCoachProgramFull(isEdit ? id! : null, isEdit);
  const createMutation = useCreateCoachProgram();
  const bulkSaveMutation = useBulkSaveCoachProgram(isEdit ? id! : null);

  // Pre-fill in modalità edit
  useEffect(() => {
    if (isEdit && programFullQuery.data && !loadedFromServer) {
      const { meta: m, sessions: ss } = programToDraft(programFullQuery.data.program);
      setMeta(m);
      setSessions(ss);
      setLoadedFromServer(true);
    }
  }, [isEdit, programFullQuery.data, loadedFromServer]);

  // Pre-fill athlete_id da query string (nuovo programma da pagina atleta)
  useEffect(() => {
    if (!isEdit && preselectAthleteId && !meta.athlete_id) {
      setMeta((prev) => ({ ...prev, athlete_id: preselectAthleteId }));
    }
  }, [isEdit, preselectAthleteId, meta.athlete_id]);

  const selectedAthlete = useMemo(
    () => (athletes || []).find((a) => a.id === meta.athlete_id) || null,
    [athletes, meta.athlete_id]
  );

  // ─── Validazione per step ───────────────────────────────────────────────
  const step1Valid = !!meta.athlete_id && !!meta.name.trim() && meta.weeks_total >= 1 && meta.weeks_total <= 52;
  const step2Valid = sessions.length > 0 && sessions.every((s) =>
    !!s.name.trim() && s.week_n >= 1 && s.week_n <= meta.weeks_total && s.day_n >= 1 && s.day_n <= 7
  );
  const step3Valid = sessions.every((s) =>
    s.exercises.length === 0 ||
    s.exercises.every((e) => !!e.name.trim() && e.sets_planned >= 1)
  );

  const canSave = step1Valid && step2Valid && step3Valid;

  // ─── Step nav ───────────────────────────────────────────────────────────
  const goNext = () => {
    setError(null);
    if (step === 1 && !step1Valid) {
      setError('Compila atleta, nome programma e settimane (1-52)');
      return;
    }
    if (step === 2 && !step2Valid) {
      setError('Aggiungi almeno una sessione con nome valido');
      return;
    }
    if (step === 3 && !step3Valid) {
      setError('Tutti gli esercizi devono avere nome e set ≥ 1');
      return;
    }
    if (step < 4) setStep(((step + 1) as Step));
  };
  const goPrev = () => {
    setError(null);
    if (step > 1) setStep(((step - 1) as Step));
  };

  // ─── Schedule helpers (step 2) ──────────────────────────────────────────
  const addSession = (week_n: number, day_n: number) => {
    setSessions((prev) => [...prev, newSessionDraft(week_n, day_n)]);
  };
  const updateSession = (idx: number, next: BulkSessionInput) => {
    setSessions((prev) => prev.map((s, i) => (i === idx ? next : s)));
  };
  const removeSession = (idx: number) => {
    setSessions((prev) => prev.filter((_, i) => i !== idx));
  };

  // ─── Save (step 4) ──────────────────────────────────────────────────────
  const handleSave = () => {
    setError(null);
    if (!canSave) {
      setError('Compila tutti i campi obbligatori prima di salvare');
      return;
    }

    // Build payload
    const buildSessions = sessions.map((s) => ({
      id: s.id,
      week_n: s.week_n,
      day_n: s.day_n,
      name: s.name.trim(),
      focus: s.focus,
      scheduled_date: s.scheduled_date,
      scheduled_time: s.scheduled_time,
      duration_estimated_min: s.duration_estimated_min,
      coach_note: s.coach_note,
      exercises: s.exercises.map((e) => ({
        id: e.id,
        position: e.position,
        name: e.name.trim(),
        variant: e.variant,
        sets_planned: e.sets_planned,
        reps_target: e.reps_target,
        rest_seconds: e.rest_seconds,
        notes: e.notes,
      })),
    }));

    if (isEdit && id) {
      const body: BulkSaveBody = {
        name: meta.name.trim(),
        goal: meta.goal || null,
        weeks_total: meta.weeks_total,
        start_date: meta.start_date || null,
        end_date: meta.end_date || null,
        status: meta.status,
        sessions: buildSessions,
      };
      void (async () => {
        try {
          await bulkSaveMutation.mutateAsync(body);
          navigate('/workout');
        } catch (err: unknown) {
          const e = err as ApiError | Error;
          setError(e instanceof ApiError && e.details ? `${e.message} — ${e.details}` : e.message || String(err));
        }
      })();
    } else {
      // CREATE: prima crea program, poi se ha sessioni fai bulk-save
      const createBody: CreateProgramBody = {
        athlete_id: meta.athlete_id,
        name: meta.name.trim(),
        goal: meta.goal || null,
        weeks_total: meta.weeks_total,
        start_date: meta.start_date || null,
        end_date: meta.end_date || null,
        status: meta.status,
      };
      void (async () => {
        try {
          const created = await createMutation.mutateAsync(createBody);
          const newProgramId = created.program.id;
          if (buildSessions.length > 0) {
            const bulkBody: BulkSaveBody = {
              name: meta.name.trim(),
              goal: meta.goal || null,
              weeks_total: meta.weeks_total,
              start_date: meta.start_date || null,
              end_date: meta.end_date || null,
              status: meta.status,
              sessions: buildSessions,
            };
            // Manual call: bulk-save su id appena creato
            const res = await fetch(`${import.meta.env.VITE_API_BASE || 'https://dietamenu.schedy.app'}/api/pro/coach/program/${newProgramId}/bulk-save`, {
              method: 'POST',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(bulkBody),
            });
            if (!res.ok) {
              const errText = await res.text().catch(() => '');
              throw new Error(`Bulk save fallita: ${res.status} ${errText}`);
            }
          }
          navigate('/workout');
        } catch (err: unknown) {
          const e = err as ApiError | Error;
          setError(e instanceof ApiError && e.details ? `${e.message} — ${e.details}` : e.message || String(err));
        }
      })();
    }
  };

  const saving = createMutation.isPending || bulkSaveMutation.isPending;
  const loading = isEdit && programFullQuery.isLoading;
  const loadError = isEdit && programFullQuery.isError;

  if (role !== 'coach') {
    return (
      <>
        <ProTopBar />
        <div className={styles.card} style={{ marginTop: 16 }}>
          <div className={styles.empty} style={{ padding: '48px 32px' }}>
            Editor disponibile solo per professionisti con ruolo Coach.
          </div>
        </div>
      </>
    );
  }

  if (loading) {
    return (
      <>
        <ProTopBar />
        <div className={styles.card} style={{ marginTop: 16 }}>
          <div className={styles.empty}>Caricamento programma…</div>
        </div>
      </>
    );
  }

  if (loadError) {
    return (
      <>
        <ProTopBar />
        <div className={styles.errorBox}>Programma non trovato o non accessibile.</div>
        <button type="button" onClick={() => navigate('/workout')} className={styles.ghostBtn}>
          Torna a Workout
        </button>
      </>
    );
  }

  return (
    <>
      <ProTopBar />

      <div style={{ padding: '4px 0 18px' }}>
        <div
          style={{
            fontFamily: 'var(--mono)',
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: 1.6,
            color: 'var(--mute)',
            textTransform: 'uppercase',
          }}
        >
          Studio Pro · Workout · {isEdit ? 'Modifica' : 'Nuovo programma'}
        </div>
        <h1
          style={{
            fontFamily: 'var(--display)',
            fontWeight: 800,
            fontSize: 32,
            letterSpacing: -1,
            margin: '6px 0 0',
            lineHeight: 1.05,
          }}
        >
          {isEdit ? meta.name || 'Modifica programma' : 'Nuovo programma'}
        </h1>
      </div>

      {/* Step indicator */}
      <div
        style={{
          display: 'flex',
          gap: 8,
          marginBottom: 18,
          padding: 4,
          background: 'var(--surface-2)',
          borderRadius: 10,
        }}
      >
        {STEPS.map((s) => {
          const active = step === s.n;
          const done = step > s.n;
          return (
            <div
              key={s.n}
              style={{
                flex: 1,
                padding: '10px 12px',
                background: active ? 'var(--surface)' : 'transparent',
                borderRadius: 8,
                boxShadow: active ? 'var(--shadow-sm)' : 'none',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <div
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 6,
                  background: done ? 'var(--green-deep)' : active ? 'var(--ink)' : 'var(--line)',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: 'var(--mono)',
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                {done ? <Check size={12} /> : s.n}
              </div>
              <div
                style={{
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: active ? 'var(--ink)' : done ? 'var(--green-deep)' : 'var(--mute)',
                }}
              >{s.label}</div>
            </div>
          );
        })}
      </div>

      {error && (
        <div className={styles.errorBox} role="alert">
          {error}
        </div>
      )}

      {/* Step content */}
      {step === 1 && (
        <Step1Meta
          meta={meta}
          setMeta={setMeta}
          athletes={athletes || []}
          athletesLoading={athletesLoading}
          isEdit={isEdit}
        />
      )}

      {step === 2 && (
        <Step2Schedule
          meta={meta}
          sessions={sessions}
          onAddSession={addSession}
          onRemoveSession={removeSession}
          onUpdateSession={updateSession}
        />
      )}

      {step === 3 && (
        <Step3Exercises
          meta={meta}
          sessions={sessions}
          onUpdateSession={updateSession}
          onRemoveSession={removeSession}
        />
      )}

      {step === 4 && (
        <Step4Review
          meta={meta}
          sessions={sessions}
          athleteName={selectedAthlete?.name || meta.athlete_id}
        />
      )}

      {/* Footer nav */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          marginTop: 22,
          paddingTop: 16,
          borderTop: '1px solid var(--line-3)',
        }}
      >
        <button
          type="button"
          onClick={() => navigate('/workout')}
          style={{
            border: '1px solid var(--line)',
            background: 'var(--surface)',
            color: 'var(--ink-2)',
            padding: '9px 14px',
            borderRadius: 8,
            fontWeight: 600,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <X size={14} /> Annulla
        </button>
        <div style={{ flex: 1 }} />
        {step > 1 && (
          <button
            type="button"
            onClick={goPrev}
            style={{
              border: '1px solid var(--line)',
              background: 'var(--surface)',
              color: 'var(--ink)',
              padding: '9px 14px',
              borderRadius: 8,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              marginRight: 8,
            }}
          >
            <ChevronLeft size={14} /> Indietro
          </button>
        )}
        {step < 4 && (
          <button
            type="button"
            onClick={goNext}
            style={{
              border: 0,
              background: 'var(--ink)',
              color: '#fff',
              padding: '9px 18px',
              borderRadius: 8,
              fontWeight: 700,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            Avanti <ChevronRight size={14} />
          </button>
        )}
        {step === 4 && (
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave || saving}
            style={{
              border: 0,
              background: canSave && !saving ? 'var(--green-deep)' : 'var(--line)',
              color: '#fff',
              padding: '9px 18px',
              borderRadius: 8,
              fontWeight: 700,
              cursor: canSave && !saving ? 'pointer' : 'not-allowed',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <Save size={14} />
            {saving ? 'Salvataggio…' : isEdit ? 'Salva modifiche' : 'Crea programma'}
          </button>
        )}
      </div>
    </>
  );
}

// ===========================================================================
// STEP 1 — Atleta & meta
// ===========================================================================
function Step1Meta({
  meta,
  setMeta,
  athletes,
  athletesLoading,
  isEdit,
}: {
  meta: DraftMeta;
  setMeta: React.Dispatch<React.SetStateAction<DraftMeta>>;
  athletes: Array<{ id: string; name: string; goal: string | null }>;
  athletesLoading: boolean;
  isEdit: boolean;
}) {
  return (
    <section className={styles.card} style={{ padding: 20 }}>
      <h2
        style={{
          fontFamily: 'var(--display)',
          fontWeight: 700,
          fontSize: 18,
          margin: '0 0 16px',
        }}
      >Atleta & dettagli programma</h2>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <FieldBlock label="Atleta">
          {isEdit ? (
            <input
              type="text"
              value={athletes.find((a) => a.id === meta.athlete_id)?.name || meta.athlete_id}
              disabled
              style={{ ...inputStyle, opacity: 0.7 }}
            />
          ) : (
            <select
              value={meta.athlete_id}
              onChange={(e) => setMeta((p) => ({ ...p, athlete_id: e.target.value }))}
              disabled={athletesLoading}
              style={inputStyle}
            >
              <option value="">{athletesLoading ? 'Caricamento…' : 'Seleziona atleta'}</option>
              {athletes.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          )}
        </FieldBlock>

        <FieldBlock label="Nome programma">
          <input
            type="text"
            value={meta.name}
            onChange={(e) => setMeta((p) => ({ ...p, name: e.target.value }))}
            placeholder="es. Massa Q2 2026"
            style={inputStyle}
          />
        </FieldBlock>

        <FieldBlock label="Goal">
          <select
            value={meta.goal}
            onChange={(e) => setMeta((p) => ({ ...p, goal: e.target.value }))}
            style={inputStyle}
          >
            <option value="">— scegli goal —</option>
            {GOAL_OPTIONS.map((g) => (
              <option key={g.value} value={g.value}>{g.label}</option>
            ))}
          </select>
        </FieldBlock>

        <FieldBlock label="Settimane (1-52)">
          <input
            type="number"
            min={1}
            max={52}
            value={meta.weeks_total}
            onChange={(e) => {
              const v = parseInt(e.target.value, 10);
              setMeta((p) => ({ ...p, weeks_total: isNaN(v) ? 1 : Math.max(1, Math.min(52, v)) }));
            }}
            style={inputStyle}
          />
        </FieldBlock>

        <FieldBlock label="Data inizio (opzionale)">
          <input
            type="date"
            value={meta.start_date}
            onChange={(e) => setMeta((p) => ({ ...p, start_date: e.target.value }))}
            style={inputStyle}
          />
        </FieldBlock>

        <FieldBlock label="Data fine (opzionale)">
          <input
            type="date"
            value={meta.end_date}
            onChange={(e) => setMeta((p) => ({ ...p, end_date: e.target.value }))}
            style={inputStyle}
          />
        </FieldBlock>

        <FieldBlock label="Stato">
          <select
            value={meta.status}
            onChange={(e) => setMeta((p) => ({ ...p, status: e.target.value as WorkoutProgramStatus }))}
            style={inputStyle}
          >
            <option value="draft">Bozza</option>
            <option value="active">Attivo</option>
            <option value="paused">In pausa</option>
            <option value="completed">Completato</option>
            <option value="archived">Archiviato</option>
          </select>
        </FieldBlock>
      </div>
      {meta.status === 'active' && (
        <div style={{ marginTop: 12, fontSize: 12.5, color: 'var(--orange-deep)' }}>
          ⚠ Impostando "Attivo" eventuali altri programmi attivi dello stesso atleta verranno archiviati automaticamente.
        </div>
      )}
    </section>
  );
}

// ===========================================================================
// STEP 2 — Schedule
// ===========================================================================
function Step2Schedule({
  meta,
  sessions,
  onAddSession,
  onRemoveSession,
  onUpdateSession,
}: {
  meta: DraftMeta;
  sessions: BulkSessionInput[];
  onAddSession: (week_n: number, day_n: number) => void;
  onRemoveSession: (idx: number) => void;
  onUpdateSession: (idx: number, next: BulkSessionInput) => void;
}) {
  const [selectedWeek, setSelectedWeek] = useState(1);

  // Indici originali per onUpdate
  const sessionsWithIdx = sessions.map((s, i) => ({ s, i }));
  const weekSessions = sessionsWithIdx.filter(({ s }) => s.week_n === selectedWeek);

  return (
    <section className={styles.card} style={{ padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <h2 style={{ fontFamily: 'var(--display)', fontWeight: 700, fontSize: 18, margin: 0 }}>
          Schedule sessioni
        </h2>
        <div style={{ flex: 1 }} />
        <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--mute)', fontWeight: 600 }}>
          {sessions.length} sessioni totali
        </div>
      </div>

      {/* Week selector */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
        {Array.from({ length: meta.weeks_total }, (_, i) => i + 1).map((w) => {
          const count = sessions.filter((s) => s.week_n === w).length;
          const active = w === selectedWeek;
          return (
            <button
              key={w}
              type="button"
              onClick={() => setSelectedWeek(w)}
              style={{
                padding: '6px 12px',
                borderRadius: 6,
                border: '1px solid var(--line)',
                background: active ? 'var(--ink)' : 'var(--surface)',
                color: active ? '#fff' : 'var(--ink-2)',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'var(--mono)',
                letterSpacing: 0.5,
              }}
            >
              S{w}
              {count > 0 && (
                <span
                  style={{
                    marginLeft: 6,
                    padding: '1px 6px',
                    background: active ? 'rgba(255,255,255,0.2)' : 'var(--surface-2)',
                    borderRadius: 4,
                    fontSize: 10,
                  }}
                >{count}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Day grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: 8,
        }}
      >
        {[1, 2, 3, 4, 5, 6, 7].map((d) => {
          const daySessions = weekSessions.filter(({ s }) => s.day_n === d);
          return (
            <div
              key={d}
              style={{
                background: 'var(--surface-2)',
                borderRadius: 8,
                padding: 10,
                minHeight: 100,
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
              }}
            >
              <div
                style={{
                  fontFamily: 'var(--mono)',
                  fontSize: 10,
                  fontWeight: 700,
                  color: 'var(--mute)',
                  letterSpacing: 1.2,
                  textTransform: 'uppercase',
                  marginBottom: 4,
                }}
              >{['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'][d - 1]}</div>
              {daySessions.map(({ s, i }) => (
                <div
                  key={s.id || i}
                  style={{
                    background: 'var(--surface)',
                    border: '1px solid var(--line)',
                    borderRadius: 6,
                    padding: '6px 8px',
                    fontSize: 11.5,
                    fontWeight: 600,
                    color: 'var(--ink)',
                    cursor: 'default',
                  }}
                  title={`${s.name || '(nuova)'}${s.focus ? ' · ' + s.focus : ''}`}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.name || '(senza nome)'}
                    </div>
                    <button
                      type="button"
                      onClick={() => onRemoveSession(i)}
                      aria-label="Rimuovi"
                      style={{
                        border: 0,
                        background: 'transparent',
                        color: 'var(--red)',
                        cursor: 'pointer',
                        padding: 0,
                        marginLeft: 4,
                        fontSize: 14,
                        lineHeight: 1,
                      }}
                    >×</button>
                  </div>
                  {s.focus && (
                    <div
                      style={{
                        fontFamily: 'var(--mono)',
                        fontSize: 9.5,
                        color: 'var(--mute)',
                        marginTop: 2,
                        textTransform: 'uppercase',
                        letterSpacing: 0.6,
                      }}
                    >{s.focus}</div>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => onAddSession(selectedWeek, d)}
                style={{
                  border: '1px dashed var(--line)',
                  background: 'transparent',
                  color: 'var(--mute)',
                  padding: '5px 6px',
                  borderRadius: 5,
                  fontSize: 11,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 3,
                  justifyContent: 'center',
                }}
              >
                <Plus size={11} /> Aggiungi
              </button>
            </div>
          );
        })}
      </div>

      {/* Dettaglio sessioni della week selezionata */}
      {weekSessions.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <div
            style={{
              fontFamily: 'var(--mono)',
              fontSize: 11,
              fontWeight: 700,
              color: 'var(--mute)',
              letterSpacing: 1.3,
              textTransform: 'uppercase',
              marginBottom: 10,
            }}
          >Dettagli sessioni · settimana {selectedWeek}</div>
          {weekSessions.map(({ s, i }) => (
            <WorkoutSessionEditor
              key={s.id || i}
              session={s}
              weeksTotal={meta.weeks_total}
              index={i}
              onChange={(next) => onUpdateSession(i, next)}
              onRemove={() => onRemoveSession(i)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

// ===========================================================================
// STEP 3 — Esercizi (tutte le sessioni)
// ===========================================================================
function Step3Exercises({
  meta,
  sessions,
  onUpdateSession,
  onRemoveSession,
}: {
  meta: DraftMeta;
  sessions: BulkSessionInput[];
  onUpdateSession: (idx: number, next: BulkSessionInput) => void;
  onRemoveSession: (idx: number) => void;
}) {
  if (sessions.length === 0) {
    return (
      <section className={styles.card} style={{ padding: 20 }}>
        <div className={styles.empty}>
          Nessuna sessione. Torna allo step 2 per aggiungerne.
        </div>
      </section>
    );
  }

  return (
    <section style={{ display: 'flex', flexDirection: 'column' }}>
      <div
        style={{
          fontFamily: 'var(--mono)',
          fontSize: 11,
          fontWeight: 700,
          color: 'var(--mute)',
          letterSpacing: 1.3,
          textTransform: 'uppercase',
          marginBottom: 10,
        }}
      >Definisci esercizi per ogni sessione</div>
      {sessions.map((s, i) => (
        <WorkoutSessionEditor
          key={s.id || i}
          session={s}
          weeksTotal={meta.weeks_total}
          index={i}
          onChange={(next) => onUpdateSession(i, next)}
          onRemove={() => onRemoveSession(i)}
        />
      ))}
    </section>
  );
}

// ===========================================================================
// STEP 4 — Revisione
// ===========================================================================
function Step4Review({
  meta,
  sessions,
  athleteName,
}: {
  meta: DraftMeta;
  sessions: BulkSessionInput[];
  athleteName: string;
}) {
  const totalExercises = sessions.reduce((sum, s) => sum + s.exercises.length, 0);
  const totalSets = sessions.reduce(
    (sum, s) => sum + s.exercises.reduce((a, e) => a + e.sets_planned, 0),
    0
  );

  return (
    <section className={styles.card} style={{ padding: 20 }}>
      <h2 style={{ fontFamily: 'var(--display)', fontWeight: 700, fontSize: 18, margin: '0 0 14px' }}>
        Revisione
      </h2>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 18 }}>
        <ReviewKpi label="Atleta" value={athleteName} />
        <ReviewKpi label="Settimane" value={String(meta.weeks_total)} />
        <ReviewKpi label="Sessioni" value={String(sessions.length)} />
        <ReviewKpi label="Esercizi · Set" value={`${totalExercises} · ${totalSets}`} />
      </div>

      <div style={{ marginBottom: 14 }}>
        <div
          style={{
            fontFamily: 'var(--mono)',
            fontSize: 10.5,
            fontWeight: 700,
            color: 'var(--mute)',
            letterSpacing: 1.2,
            textTransform: 'uppercase',
            marginBottom: 6,
          }}
        >Dati programma</div>
        <div style={{ fontSize: 13.5, color: 'var(--ink)', lineHeight: 1.6 }}>
          <b>{meta.name}</b>
          {meta.goal && <span style={{ marginLeft: 8, color: 'var(--mute)' }}>· {meta.goal}</span>}
          <br />
          Stato: <b>{meta.status}</b>
          {meta.start_date && <> · dal {meta.start_date}</>}
          {meta.end_date && <> al {meta.end_date}</>}
        </div>
      </div>

      <div>
        <div
          style={{
            fontFamily: 'var(--mono)',
            fontSize: 10.5,
            fontWeight: 700,
            color: 'var(--mute)',
            letterSpacing: 1.2,
            textTransform: 'uppercase',
            marginBottom: 8,
          }}
        >Sessioni</div>
        {sessions.length === 0 ? (
          <div className={styles.empty} style={{ padding: 20 }}>Nessuna sessione definita.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--surface-2)' }}>
                <th style={th}>Set.</th>
                <th style={th}>Giorno</th>
                <th style={th}>Nome</th>
                <th style={th}>Focus</th>
                <th style={th}>Esercizi</th>
                <th style={th}>Set totali</th>
              </tr>
            </thead>
            <tbody>
              {sessions
                .slice()
                .sort((a, b) => a.week_n - b.week_n || a.day_n - b.day_n)
                .map((s, i) => {
                  const setSum = s.exercises.reduce((a, e) => a + e.sets_planned, 0);
                  return (
                    <tr key={s.id || i}>
                      <td style={td}>{s.week_n}</td>
                      <td style={td}>{['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'][s.day_n - 1]}</td>
                      <td style={td}>{s.name || <em style={{ color: 'var(--mute)' }}>(senza nome)</em>}</td>
                      <td style={td}>{s.focus || '—'}</td>
                      <td style={td}>{s.exercises.length}</td>
                      <td style={td}>{setSum}</td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

const th: React.CSSProperties = {
  padding: '8px 10px',
  textAlign: 'left',
  fontFamily: 'var(--mono)',
  fontSize: 10.5,
  fontWeight: 700,
  color: 'var(--mute)',
  letterSpacing: 1.2,
  textTransform: 'uppercase',
  borderBottom: '1px solid var(--line-3)',
};

const td: React.CSSProperties = {
  padding: '8px 10px',
  borderBottom: '1px solid var(--line-3)',
  fontSize: 13,
};

function ReviewKpi({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        background: 'var(--surface-2)',
        border: '1px solid var(--line)',
        borderRadius: 10,
        padding: 12,
      }}
    >
      <div
        style={{
          fontFamily: 'var(--mono)',
          fontSize: 10,
          fontWeight: 700,
          color: 'var(--mute)',
          letterSpacing: 1.2,
          textTransform: 'uppercase',
        }}
      >{label}</div>
      <div
        style={{
          fontFamily: 'var(--display)',
          fontSize: 17,
          fontWeight: 700,
          color: 'var(--ink)',
          marginTop: 4,
          letterSpacing: -0.4,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >{value}</div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  border: '1px solid var(--line)',
  borderRadius: 6,
  padding: '8px 10px',
  fontSize: 13,
  background: 'var(--surface)',
  color: 'var(--ink)',
  fontFamily: 'var(--sans)',
  boxSizing: 'border-box',
};

function FieldBlock({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <span
        style={{
          fontFamily: 'var(--mono)',
          fontSize: 10.5,
          fontWeight: 700,
          color: 'var(--mute)',
          letterSpacing: 1.2,
          textTransform: 'uppercase',
        }}
      >{label}</span>
      {children}
    </label>
  );
}
